import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ImagePlus, Loader2, Download, RefreshCw, Trash2, Upload, Sparkles, X, Scissors } from "lucide-react";
import { getToken } from "../lib/api";

const ASPECTS = [
  { k: "1:1", label: "Quadrat (1:1)" },
  { k: "4:5", label: "Portrait (4:5)" },
  { k: "16:9", label: "Landscape (16:9)" },
  { k: "9:16", label: "Story (9:16)" },
];

export default function BildStudio() {
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("1:1");
  const [brand, setBrand] = useState(true);
  const [reference, setReference] = useState(null); // {id, url, filename}
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [trim, setTrim] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/images/gallery");
      setGallery(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const absUrl = (u) => (u?.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);

  const uploadReference = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = getToken();
      const res = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload fehlgeschlagen");
      const data = await res.json();
      const isVideo = (data.mime || "").startsWith("video/");
      if (!isVideo) setReference({ id: data.id, url: data.url, filename: data.filename });
      toast.success(isVideo ? "Video hochgeladen" : "Referenzbild hochgeladen");
      load();
    } catch (e) {
      toast.error(e.message || "Upload fehlgeschlagen");
    }
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const payload = {
        prompt, aspect_ratio: aspect, brand_preset: brand,
        reference_id: reference?.id || null, quality: "low",
      };
      await api.post("/images/generate", payload);
      toast.success("Bild erzeugt");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Bildgenerierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const removeImage = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/images/${confirmDel.id}`);
      toast.success(((confirmDel.mime || "").startsWith("video/")) ? "Video gelöscht" : "Bild gelöscht");
      setConfirmDel(null);
      if (reference?.id === confirmDel.id) setReference(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const applyAsReference = (img) => {
    setReference({ id: img.id, url: img.url, filename: img.filename });
    toast.success("Als Referenz übernommen");
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="bildstudio-page">
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Kreativ-Werkstatt</div>
        <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Bild-Studio</h1>
        <p className="text-sm text-slate-400 mt-2">
          Produktbilder und Werbemotive per KI — ein Prompt reicht.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6">
        {/* Composer */}
        <Card className="bg-[#111827] border-white/5 h-fit" data-testid="bildstudio-composer">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-slate-300 text-xs">Prompt</Label>
              <Textarea
                data-testid="bildstudio-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder="z. B. Produktfoto Gesichtscreme auf Marmoroberfläche, weiches natürliches Licht, luxuriös, 4K"
                className="mt-1 bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label className="text-slate-300 text-xs mb-2 block">Seitenverhältnis</Label>
              <div className="flex flex-wrap gap-2" data-testid="bildstudio-aspect-group">
                {ASPECTS.map((a) => (
                  <button
                    key={a.k}
                    data-testid={`aspect-${a.k.replace(":","-")}`}
                    type="button"
                    onClick={() => setAspect(a.k)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition
                      ${aspect === a.k
                        ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                        : "bg-transparent border-white/10 text-slate-400 hover:text-white"}`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between py-1">
              <div>
                <Label className="text-slate-300 text-xs">Markenstil</Label>
                <div className="text-[11px] text-slate-500">Minimalistisch, warm, editorial</div>
              </div>
              <Switch checked={brand} onCheckedChange={setBrand} data-testid="brand-switch" />
            </div>

            <div>
              <Label className="text-slate-300 text-xs mb-1 block">Referenzbild (optional)</Label>
              {reference ? (
                <div className="flex items-center gap-3 rounded-lg border border-white/10 p-2 bg-white/[0.03]" data-testid="bildstudio-reference">
                  <img src={absUrl(reference.url)} alt="ref" className="h-14 w-14 object-cover rounded" />
                  <div className="text-xs text-slate-400 flex-1 truncate">{reference.filename}</div>
                  <Button variant="ghost" size="icon" onClick={() => setReference(null)}
                    data-testid="bildstudio-reference-remove" className="text-slate-400 hover:text-white">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,video/mp4,video/quicktime,video/webm,video/x-matroska"
                    className="hidden"
                    data-testid="bildstudio-reference-input"
                    onChange={(e) => e.target.files?.[0] && uploadReference(e.target.files[0])}
                  />
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                    data-testid="bildstudio-upload-reference"
                    className="border-white/10 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white w-full">
                    <Upload className="h-4 w-4 mr-2" /> Bild oder Video hochladen
                  </Button>
                </>
              )}
            </div>

            <Button
              onClick={generate}
              disabled={generating || !prompt.trim()}
              data-testid="bildstudio-generate-button"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20"
            >
              {generating
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Erzeuge Bild…</>
                : <><Sparkles className="h-4 w-4 mr-2" /> Bild erstellen</>}
            </Button>

            <div className="text-[10px] text-slate-500">
              Qualität: Standard · Dauer ~10–25 s.
            </div>
          </CardContent>
        </Card>

        {/* Gallery */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold tracking-tight text-white">Galerie</h2>
            <div className="text-xs text-slate-500" data-testid="gallery-count">{gallery.length} Bilder</div>
          </div>

          {loading ? (
            <div className="text-sm text-slate-400">Lade…</div>
          ) : gallery.length === 0 ? (
            <Card className="bg-[#111827] border-white/5" data-testid="gallery-empty">
              <CardContent className="p-10 text-center">
                <ImagePlus className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <div className="text-white font-semibold mb-1">Noch keine Bilder</div>
                <div className="text-sm text-slate-400">Erzeuge dein erstes Bild oder lade eine Referenz hoch.</div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="gallery-grid">
              {gallery.map((img) => (
                <GalleryTile
                  key={img.id}
                  img={img}
                  absUrl={absUrl}
                  onUseRef={() => applyAsReference(img)}
                  onDelete={() => setConfirmDel(img)}
                  onTrim={() => setTrim(img)}
                />
              ))}
            </div>
          )}
      <TrimDialog clip={trim} onClose={() => setTrim(null)} onDone={() => { setTrim(null); load(); }} absUrl={absUrl} token={getToken()} />


        </div>
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>{(confirmDel?.mime || "").startsWith("video/") ? "Video" : "Bild"} löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="image-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={removeImage} data-testid="image-delete-confirm"
              className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function GalleryTile({ img, absUrl, onUseRef, onDelete, onTrim }) {
  const isVideo = (img.media_type === "video") || ((img.mime || "").startsWith("video/"));
  return (
    <div className="group relative rounded-lg overflow-hidden border border-white/5 bg-black/40"
      data-testid={`gallery-item-${img.id}`}>
      {isVideo ? (
        <video src={absUrl(img.url)} muted playsInline preload="metadata"
          className="w-full aspect-square object-cover" data-testid={`gallery-video-${img.id}`} />
      ) : (
        <img src={absUrl(img.url)} alt={img.prompt || img.filename}
          className="w-full aspect-square object-cover" />
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col justify-end p-2">
        <div className="text-[11px] text-slate-200 line-clamp-3 mb-2">{img.prompt || img.filename}</div>
        <div className="flex items-center gap-1">
          <a href={absUrl(img.url)} download={img.filename}
            data-testid={`gallery-download-${img.id}`}
            className="flex-1 text-center text-xs bg-white/10 hover:bg-white/20 text-white rounded px-2 py-1 transition">
            <Download className="h-3 w-3 inline mr-1" /> Download
          </a>
          {isVideo ? (
            <Button size="icon" variant="ghost" onClick={() => onTrim(img)}
              data-testid={`gallery-trim-${img.id}`}
              title="Schneiden"
              className="h-7 w-7 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/20">
              <Scissors className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button size="icon" variant="ghost" onClick={onUseRef}
              data-testid={`gallery-use-ref-${img.id}`}
              className="h-7 w-7 text-slate-200 hover:text-white hover:bg-white/10">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button size="icon" variant="ghost" onClick={onDelete}
            data-testid={`gallery-delete-${img.id}`}
            className="h-7 w-7 text-red-300 hover:text-red-200 hover:bg-red-500/20">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {isVideo ? (
        <Badge className="absolute top-2 left-2 bg-indigo-500/80 text-white border-0 text-[9px]">
          Video
        </Badge>
      ) : img.kind === "generated" && (
        <Badge className="absolute top-2 left-2 bg-emerald-500/80 text-white border-0 text-[9px]">
          {img.aspect_ratio || "KI"}
        </Badge>
      )}
    </div>
  );
}


function TrimDialog({ clip, onClose, onDone, absUrl, token }) {
  const videoRef = useRef(null);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [saving, setSaving] = useState(false);
  const [duration, setDuration] = useState(clip?.duration_sec || 0);

  useEffect(() => {
    if (!clip) return;
    const d = clip.duration_sec || 0;
    setDuration(d);
    setStart(0);
    setEnd(d);
  }, [clip?.id]);

  if (!clip) return null;
  const seg = Math.max(0, Number(end) - Number(start));
  const valid = seg >= 0.5 && end > start && start >= 0 && end <= duration + 0.05;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await api.post(`/files/${clip.id}/trim`, { start_sec: Number(start), end_sec: Number(end) });
      toast.success("Schnitt gespeichert");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Schnitt fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!clip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-xl" data-testid="trim-dialog">
        <DialogHeader>
          <DialogTitle>Video schneiden</DialogTitle>
          <DialogDescription className="text-slate-400">
            Wähle Start und Ende — wir speichern das Teilstück als neue Datei. Das Original bleibt.
          </DialogDescription>
        </DialogHeader>
        <video
          ref={videoRef}
          src={absUrl(`/api/files/${clip.id}?token=${token}`)}
          controls
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration || clip.duration_sec || 0;
            setDuration(d);
            if (end === 0 || end > d) setEnd(d);
          }}
          className="w-full max-h-72 bg-black rounded border border-white/10"
          data-testid="trim-preview"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Start (Sekunden)</Label>
            <Input type="number" step="0.1" min="0" max={duration}
              value={start}
              onChange={(e) => setStart(Math.max(0, Number(e.target.value)))}
              data-testid="trim-start"
              className="bg-white/5 border-white/10 text-slate-100" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Ende (Sekunden)</Label>
            <Input type="number" step="0.1" min="0" max={duration}
              value={end}
              onChange={(e) => setEnd(Number(e.target.value))}
              data-testid="trim-end"
              className="bg-white/5 border-white/10 text-slate-100" />
          </div>
        </div>
        <div className="text-xs text-slate-400" data-testid="trim-duration">
          Neues Teilstück: <strong className="text-emerald-400">{seg.toFixed(1)} s</strong>
          {" · "}
          Original: {duration.toFixed(1)} s
          {!valid && <span className="text-red-400 ml-2">· Bereich ungültig (mind. 0,5 s)</span>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="trim-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={!valid || saving}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
            data-testid="trim-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Speichere…</> : "Schnitt speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
